import React, { useState, useEffect } from 'react';
import { getFileBlob } from '../shared/apiClient';

interface SecureImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  onBlobReady?: (blobUrl: string) => void;
  onImageClick?: (blobUrl: string) => void;
}

export function SecureImage({ src, alt = '', className, onBlobReady, onImageClick, ...props }: SecureImageProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const onBlobReadyRef = React.useRef(onBlobReady);
  useEffect(() => {
    onBlobReadyRef.current = onBlobReady;
  }, [onBlobReady]);

  useEffect(() => {
    let active = true;
    let currentUrl: string | null = null;

    if (!src) {
      setBlobUrl(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(false);

    getFileBlob(src)
      .then(({ url }) => {
        if (!active) {
          URL.revokeObjectURL(url);
          return;
        }
        currentUrl = url;
        setBlobUrl(url);
        if (onBlobReadyRef.current) onBlobReadyRef.current(url);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      active = false;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [src]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center bg-slate-100 rounded-lg animate-pulse min-w-10 min-h-10 ${className || ''}`}>
        <span className="text-[10px] text-slate-400">...</span>
      </div>
    );
  }

  if (error || !blobUrl) {
    return (
      <div className={`flex items-center justify-center bg-slate-100 rounded-lg text-red-500 min-w-10 min-h-10 ${className || ''}`}>
        <span className="text-[10px] font-bold">!</span>
      </div>
    );
  }

  return (
    <img
      src={blobUrl}
      alt={alt}
      className={className}
      onClick={() => onImageClick && onImageClick(blobUrl)}
      {...props}
    />
  );
}
